import { useDocumentMeta } from '@/hooks/useDocumentMeta';
import { routeMeta } from '@/content/site';
import { articleJsonLd, breadcrumbJsonLd, organizationJsonLd, webPageJsonLd, websiteJsonLd } from '@/lib/seo';

interface SeoProps {
  /** 站内路径，用于 canonical 与默认标题。 */
  path: string;
  title?: string;
  description?: string;
  type?: 'website' | 'article';
  publishedAt?: string;
  /** 文章正文里的标签，会写进结构化数据的 keywords。 */
  tags?: readonly string[];
  crumbs?: readonly { name: string; path?: string }[];
}

/**
 * 页面级 SEO。默认从 `site.json` 的路由元信息取标题与描述 —— 那份文件同时供
 * sitemap 脚本使用，所以页面标题与 sitemap 不会各说各话。
 */
export function Seo({ path, title, description, type = 'website', publishedAt, tags, crumbs }: SeoProps) {
  const meta = routeMeta(path);

  const graph: object[] = [organizationJsonLd(), websiteJsonLd()];

  if (type === 'article' && title && description && publishedAt) {
    graph.push(articleJsonLd({ slug: path.split('/').pop() ?? '', title, description, date: publishedAt, tags: tags ?? [] }));
  } else {
    graph.push(
      webPageJsonLd({
        path,
        title: title ?? meta?.title ?? 'Havorix™',
        description: description ?? meta?.description ?? '',
      }),
    );
  }

  if (crumbs && crumbs.length > 1) {
    graph.push(breadcrumbJsonLd(crumbs.filter((crumb) => crumb.path) as { name: string; path: string }[]));
  }

  useDocumentMeta({
    title: title ?? meta?.title ?? 'Havorix™ — 低空经济运营监管平台',
    description: description ?? meta?.description ?? '',
    path,
    type,
    publishedAt,
    jsonLd: graph,
  });

  return null;
}
